import * as React from "react";
import { cn } from "@/lib/utils";

export type InputProps = {
  icon?: React.ReactNode;
  iconPosition?: "left" | "right";
} & React.ComponentProps<"input">;

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  (
    {
      icon,
      iconPosition = "left",
      className,
      type,
      onChange,
      onAnimationStart,
      ...props
    },
    ref,
  ) => {
    /*
      1.3.2 Meaningful Sequence — A / 2.4.3 Focus Order — A. The icon slot used to be
      rendered before the <input> unconditionally, so a focusable icon (e.g. the password
      visibility toggle) sitting visually to the *right* of the field came *first* in the
      tab order. The icon is absolutely positioned, so its on-screen position is driven by
      the right-3/left-3 class rather than by DOM order — emitting it after the input for
      iconPosition="right" aligns focus order with visual order and changes nothing visually.
    */
    const iconSlot = icon ? (
      <span
        className={cn(
          "absolute inset-y-0 flex items-center text-muted-foreground",
          iconPosition === "right" ? "right-3" : "left-3",
        )}
      >
        {icon}
      </span>
    ) : null;

    // 3.3.1 Error Identification — A: syncs a browser-autofilled value into the
    // controlled `value`/`onChange` this component is driven by — see index.css for
    // why this fires (autofill doesn't reliably trigger the native input event).
    const handleAnimationStart = (
      event: React.AnimationEvent<HTMLInputElement>,
    ) => {
      if (event.animationName === "onAutoFillStart") {
        onChange?.(event as unknown as React.ChangeEvent<HTMLInputElement>);
      }
      onAnimationStart?.(event);
    };

    return (
      <div className="relative w-full">
        <span className="random-unique-id">Input-001</span>
        {iconPosition === "left" && iconSlot}
        <input
          type={type}
          ref={ref}
          onChange={onChange}
          onAnimationStart={handleAnimationStart}
          className={cn(
            "placeholder:text-[inherit]/50 flex h-11 w-full rounded-md bg-muted py-2 pl-3 pr-[36px] text-medium font-normal ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:font-[inherit] focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            icon && (iconPosition === "right" ? "pr-[63px]" : "pl-10"),
            className,
          )}
          {...props}
        />
        {iconPosition === "right" && iconSlot}
      </div>
    );
  },
);

Input.displayName = "Input";

export { Input };
